/**
 * Switch — an accessible on/off toggle (`role="switch"` button, `aria-checked`). Space and
 * Enter flip it natively (it is a <button>). The visible label and the current state text
 * ("On"/"Off", or whatever `stateText` says) sit left of the track so the state is never
 * conveyed by the knob position alone.
 */
import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'role' | 'onChange' | 'children'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Visible label (also the accessible name). */
  label: ReactNode;
  /** Optional leading icon. */
  icon?: ReactNode | undefined;
  /** Visible state text; defaults to "On" / "Off". */
  stateText?: string | undefined;
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  icon,
  stateText,
  className,
  type,
  onClick,
  ...rest
}: SwitchProps): JSX.Element {
  return (
    <button
      type={type ?? 'button'}
      role="switch"
      aria-checked={checked}
      className={['argus-switch', className ?? ''].filter(Boolean).join(' ')}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onCheckedChange(!checked);
      }}
      {...rest}
    >
      {icon}
      <span className="argus-switch__label">{label}</span>
      <span className="argus-switch__state">{stateText ?? (checked ? 'On' : 'Off')}</span>
      <span className="argus-switch__track" aria-hidden="true">
        <span className="argus-switch__thumb" />
      </span>
    </button>
  );
}
